import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Popover,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AccessTime as HoursIcon,
  ConfirmationNumberOutlined as TicketIcon,
  DateRange as RangeIcon,
  Download as DownloadIcon,
  EventAvailable as DaysIcon,
  FolderOutlined as ProjectIcon,
  Groups as PeopleIcon,
  ListAlt as WorklogsIcon,
  LocalOfferOutlined as ActivityIcon,
  PersonOutline as PersonIcon,
} from '@mui/icons-material';
import { UserContext } from '../components/MiniDrawer';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import MonthPicker from '../components/monthly/MonthPicker';
import PageHeader, { PageContainer } from '../components/PageHeader';
import MultiSelectFilter, { SearchFilter, segmentedSx } from '../components/monthly/MultiSelectFilter';
import { MONTH_NAMES } from '../components/monthly/format';
import { useFillViewportHeight } from '../components/monthly/tableStyles';
import StatCard from '../components/home/StatCard';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK } from '../components/home/homeTheme';
import WorklogTable, { hoursLabel } from '../components/worklogs/WorklogTable';
import ChangeTicketDialog from '../components/worklogs/ChangeTicketDialog';
import DayStrip, { countDays, MAX_STRIP_DAYS } from '../components/worklogs/DayStrip';
import { downloadWorklogsWorkbook } from '../components/worklogs/exportWorklogs';
import WorklogEditDialog from '../components/daily/WorklogEditDialog';
import { fetchListProjects, fetchWorklogs, isManagerFor, queryProjectIds, ROW_LIMIT } from '../utils/worklogsV2Service';
import { fetchHierarchy } from '../utils/monthlyPeopleService';
import { deleteWorklog, fetchAllowedActivityIds, fetchUserProjects, fetchWorklogOptions, updateWorklog } from '../utils/worklogService';
import { cleanTicketId, filterProjectsForDate, getTicketPrefix, getWorklogLockReason, validateWorklog } from '../utils/worklogRules';
import { formatMinutes, MAX_DAILY_MINUTES, parseEffort } from '../utils/effortParser';
import { parseLocalDate } from '../utils/dateHelpers';

const TITLES = { mine: 'My worklogs', projects: 'Project worklogs', people: 'People worklogs' };
const EMPTY = [];
const pad = (n) => String(n).padStart(2, '0');
const isDateKey = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const num = (n) => Number(n || 0).toLocaleString('en-GB');
const shortDate = (key) => parseLocalDate(key).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const customerName = (p) => (typeof p?.customer === 'string' ? p.customer : p?.customer?.name || '');

function monthRange(year, month) {
  const ym = `${year}-${pad(month)}`;
  return { from: `${ym}-01`, to: `${ym}-${pad(new Date(year, month, 0).getDate())}` };
}

/** { year, month } se il periodo è esattamente un mese, altrimenti null. */
function fullMonthOf(from, to) {
  const [y, m] = from.split('-').map(Number);
  const r = monthRange(y, m);
  return r.from === from && r.to === to ? { year: y, month: m } : null;
}

const distinctOptions = (rows, idKey, nameKey, captionKey) => {
  const map = new Map();
  rows.forEach((w) => {
    if (w[idKey] != null && !map.has(w[idKey])) map.set(w[idKey], { id: w[idKey], name: w[nameKey] || '—', caption: captionKey ? w[captionKey] || '' : '' });
  });
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const sortRows = (rows) =>
  [...rows].sort(
    (a, b) =>
      b.dateKey.localeCompare(a.dateKey) ||
      String(a.user_name || '').localeCompare(String(b.user_name || '')) ||
      String(a.project_name || '').localeCompare(String(b.project_name || '')),
  );

/**
 * Lista worklog V2, unica per i tre ambiti:
 * - mine: worklog dell'utente, con modifica ed eliminazione
 * - projects: worklog dei progetti gestiti, con cambio ticket sulle righe selezionate
 * - people: worklog dei riporti (diretti o tutti)
 * Il periodo si carica una volta; ricerca, filtri, totali e raggruppamento per giorno sono lato client.
 */
export default function WorklogsV2({ scope }) {
  const { selectedUser } = useContext(UserContext);
  const { isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, isDeliveryManager, currentRole } = useRoleAccess();
  const { userId } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- Periodo (nell'URL), mese corrente di default ----
  const now = new Date();
  const defaults = monthRange(now.getFullYear(), now.getMonth() + 1);
  const pFrom = searchParams.get('from');
  const pTo = searchParams.get('to');
  const validRange = isDateKey(pFrom) && isDateKey(pTo) && pTo >= pFrom;
  const from = validRange ? pFrom : defaults.from;
  const to = validRange ? pTo : defaults.to;
  const fullMonth = fullMonthOf(from, to);
  const [fromYear, fromMonth] = from.split('-').map(Number);
  const setPeriod = (f, t) => setSearchParams({ from: f, to: t }, { replace: true });
  const [rangeAnchor, setRangeAnchor] = useState(null);
  const [draftRange, setDraftRange] = useState({ from, to });

  // ---- Ambito ----
  const mineId = selectedUser && selectedUser !== 'ALL' ? selectedUser : null;
  const seesAllProjects = isDeliveryManager || currentRole === 7;
  const managesProjects = isAdmin || isServiceManager || isTeamLeader || isJRServiceManager;
  const canChangeTickets = scope === 'projects' && !isTeamLeader;

  const [projects, setProjects] = useState(EMPTY);
  const [projectsReady, setProjectsReady] = useState(scope !== 'projects');
  const [selectedProjects, setSelectedProjects] = useState(EMPTY);
  const [people, setPeople] = useState(EMPTY);
  const [peopleReady, setPeopleReady] = useState(scope !== 'people');
  const [peopleMode, setPeopleMode] = useState('direct');
  const [selectedPeople, setSelectedPeople] = useState(EMPTY);
  const [scopeError, setScopeError] = useState(null);

  useEffect(() => {
    if (scope !== 'projects' || !userId) return undefined;
    let cancelled = false;
    fetchListProjects({ seesAllProjects, managesProjects, userId })
      .then((list) => !cancelled && setProjects(list))
      .catch((e) => !cancelled && setScopeError(`Unable to load your projects: ${e.message}`))
      .finally(() => !cancelled && setProjectsReady(true));
    return () => {
      cancelled = true;
    };
  }, [scope, seesAllProjects, managesProjects, userId]);

  useEffect(() => {
    if (scope !== 'people' || !userId) return undefined;
    let cancelled = false;
    fetchHierarchy(userId)
      .then((list) => !cancelled && setPeople(list))
      .catch((e) => !cancelled && setScopeError(`Unable to load your reports: ${e.message}`))
      .finally(() => !cancelled && setPeopleReady(true));
    return () => {
      cancelled = true;
    };
  }, [scope, userId]);

  const modePeople = useMemo(() => (peopleMode === 'direct' ? people.filter((p) => p.depth === 0) : people), [people, peopleMode]);
  const peopleOptions = useMemo(() => {
    const names = new Map(people.map((p) => [String(p.id), p.name]));
    return modePeople
      .map((p) => ({ id: p.id, name: p.name, caption: p.depth > 0 ? `Reports to ${names.get(String(p.managerId)) || '—'}` : p.email || '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [people, modePeople]);

  const query = useMemo(() => {
    if (scope === 'mine') return mineId ? { userId: mineId } : null;
    if (scope === 'projects') {
      const ids = queryProjectIds(projects, selectedProjects, { seesAllProjects, managesProjects, isAdmin });
      return ids.length ? { projectIds: ids } : null;
    }
    const chosen = selectedPeople.length ? selectedPeople.filter((id) => modePeople.some((p) => p.id === id)) : modePeople.map((p) => p.id);
    return chosen.length ? { userIds: chosen } : null;
  }, [scope, mineId, projects, selectedProjects, seesAllProjects, managesProjects, isAdmin, selectedPeople, modePeople]);

  // ---- Worklog del periodo ----
  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState({ key: null, worklogs: EMPTY, total: 0 });
  const [loadError, setLoadError] = useState(null);
  const loadKey = query ? JSON.stringify({ from, to, query, refreshToken }) : null;
  const loading = !!loadKey && data.key !== loadKey && !loadError;

  useEffect(() => {
    if (!loadKey) return undefined;
    let cancelled = false;
    setLoadError(null);
    fetchWorklogs({ dateFrom: from, dateTo: to, ...query })
      .then((res) => !cancelled && setData({ key: loadKey, worklogs: sortRows(res.worklogs), total: res.total }))
      .catch((e) => !cancelled && setLoadError(`Unable to load worklogs: ${e.message}`));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  const worklogs = loadKey && data.key === loadKey ? data.worklogs : EMPTY;

  // ---- Filtri lato client ----
  const [search, setSearch] = useState('');
  const [activityFilter, setActivityFilter] = useState(EMPTY);
  const [projectFilter, setProjectFilter] = useState(EMPTY);
  const [personFilter, setPersonFilter] = useState(EMPTY);

  const activityOptions = useMemo(() => distinctOptions(worklogs, 'activity_id', 'activity_name'), [worklogs]);
  const projectOptions = useMemo(() => distinctOptions(worklogs, 'project_id', 'project_name'), [worklogs]);
  const personOptions = useMemo(() => distinctOptions(worklogs, 'user_id', 'user_name', 'team_name'), [worklogs]);

  // Giorni scelti nella barra dei giorni (dentro il periodo caricato)
  const [dayRange, setDayRange] = useState(null);
  useEffect(() => setDayRange(null), [from, to]);
  const showDayStrip = countDays(from, to) <= MAX_STRIP_DAYS;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return worklogs.filter((w) => {
      if (activityFilter.length && !activityFilter.includes(w.activity_id)) return false;
      if (projectFilter.length && !projectFilter.includes(w.project_id)) return false;
      if (personFilter.length && !personFilter.includes(w.user_id)) return false;
      if (!q) return true;
      return [w.ticket_id, w.description, w.project_name, w.user_name, w.activity_name].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [worklogs, search, activityFilter, projectFilter, personFilter]);
  const rows = useMemo(
    () => (dayRange ? filteredRows.filter((w) => w.dateKey >= dayRange.from && w.dateKey <= dayRange.to) : filteredRows),
    [filteredRows, dayRange],
  );

  const stats = useMemo(() => {
    const tickets = new Set();
    const users = new Set();
    const days = new Set();
    let minutes = 0;
    rows.forEach((w) => {
      minutes += Number(w.effort) || 0;
      const t = String(w.ticket_id || '').trim();
      if (t) tickets.add(t);
      users.add(w.user_id);
      days.add(w.dateKey);
    });
    return { minutes, tickets: tickets.size, users: users.size, days: days.size };
  }, [rows]);

  // ---- Selezione e cambio ticket (progetti) ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [changeOpen, setChangeOpen] = useState(false);
  const [options, setOptions] = useState({ activities: EMPTY, effortTypes: EMPTY });
  const [snack, setSnack] = useState(null);

  useEffect(() => setSelectedIds(new Set()), [data.key]);
  useEffect(() => {
    if (scope === 'people') return;
    fetchWorklogOptions()
      .then((o) => setOptions(o))
      .catch(() => setOptions({ activities: EMPTY, effortTypes: EMPTY }));
  }, [scope]);

  const selectedRows = useMemo(() => (selectedIds.size ? rows.filter((w) => selectedIds.has(w.id)) : EMPTY), [rows, selectedIds]);
  const managedProjects = useMemo(() => projects.filter((p) => isManagerFor(p, isAdmin)), [projects, isAdmin]);
  const toggleRow = (id) =>
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (checked) => setSelectedIds(checked ? new Set(rows.map((w) => w.id)) : new Set());
  const refresh = () => setRefreshToken((t) => t + 1);

  // ---- Modifica ed eliminazione (mie) ----
  const [userProjects, setUserProjects] = useState(EMPTY);
  const [allowedMap, setAllowedMap] = useState({});
  const [editing, setEditing] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (scope !== 'mine' || !mineId) return;
    fetchUserProjects(mineId)
      .then(setUserProjects)
      .catch(() => setUserProjects(EMPTY));
  }, [scope, mineId]);

  const findProject = useCallback((id) => userProjects.find((p) => p.id === id) || null, [userProjects]);
  const findActivity = useCallback((id) => options.activities.find((a) => a.id === id) || null, [options.activities]);
  const ensureAllowed = (projectId) => {
    if (!projectId || allowedMap[projectId] !== undefined) return;
    setAllowedMap((m) => ({ ...m, [projectId]: null }));
    fetchAllowedActivityIds(projectId).then((ids) => setAllowedMap((m) => ({ ...m, [projectId]: ids })));
  };
  const activitiesFor = (projectId) => {
    const allowed = allowedMap[projectId];
    return allowed && allowed.length ? options.activities.filter((a) => allowed.includes(a.id)) : options.activities;
  };
  const lockReason = useCallback((w) => getWorklogLockReason(w, findProject(w.project_id)), [findProject]);

  const startEdit = (w) => {
    ensureAllowed(w.project_id);
    setEditErrors({});
    setEditing({
      worklog: w,
      value: {
        projectId: w.project_id,
        activityId: w.activity_id,
        effortTypeId: w.effort_type_id,
        ticket: String(w.ticket_id || '').trim(),
        effort: String(w.effort ?? ''),
        description: w.description || '',
      },
    });
  };
  const editDate = editing ? parseLocalDate(editing.worklog.dateKey) : null;
  const editProject = editing ? findProject(editing.value.projectId) : null;
  const editActivity = editing ? findActivity(editing.value.activityId) : null;
  const editPrefix = getTicketPrefix(editProject, editActivity);
  const editProjects = editing ? filterProjectsForDate(userProjects, editDate) : EMPTY;
  const changeEdit = (patch) => {
    if (patch.projectId) ensureAllowed(patch.projectId);
    setEditing((ed) => {
      const value = { ...ed.value, ...patch };
      const newPrefix = getTicketPrefix(findProject(value.projectId), findActivity(value.activityId));
      if (('projectId' in patch || 'activityId' in patch) && newPrefix && !value.ticket.startsWith(newPrefix)) value.ticket = newPrefix + value.ticket;
      return { ...ed, value };
    });
    setEditErrors({});
  };
  const saveEdit = async () => {
    const { worklog, value: v } = editing;
    const e = validateWorklog({ project: editProject, activity: editActivity, ...v }, editPrefix, editDate);
    const others = worklogs.filter((w) => w.dateKey === worklog.dateKey && w.id !== worklog.id).reduce((sum, w) => sum + (Number(w.effort) || 0), 0);
    if (!e.effort && others + parseEffort(v.effort).minutes > MAX_DAILY_MINUTES) e.effort = 'The daily total would exceed 24h';
    setEditErrors(e);
    if (Object.keys(e).length) return;
    setEditSaving(true);
    try {
      await updateWorklog({
        id: worklog.id,
        user_id: mineId,
        project_id: v.projectId,
        activity_id: v.activityId,
        status_id: worklog.status_id || '1',
        effort_type_id: v.effortTypeId,
        effort: parseEffort(v.effort).minutes,
        ticket_id: v.ticket.trim() === editPrefix ? '' : cleanTicketId(v.ticket, editProject?.ticket_id_prefix),
        description: v.description.trim(),
        date: worklog.dateKey,
      });
      setEditing(null);
      setSnack('Worklog updated');
      refresh();
    } catch (err) {
      setEditErrors({ general: `Not saved: ${err.message}` });
    } finally {
      setEditSaving(false);
    }
  };
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteWorklog(toDelete.id);
      setToDelete(null);
      setSnack('Worklog deleted');
      refresh();
    } catch (err) {
      setSnack(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // ---- Export ----
  const exportLabel = () => {
    const nameOf = (list, id) => list.find((o) => o.id === id)?.name;
    if (scope === 'mine') return projectFilter.length === 1 ? nameOf(projectOptions, projectFilter[0]) : '';
    if (scope === 'projects') {
      const chosen = selectedProjects.length ? selectedProjects : seesAllProjects ? EMPTY : projects.map((p) => p.id);
      if (chosen.length === 1) return nameOf(projects, chosen[0]);
      return chosen.length > 1 ? 'Multiple_Projects' : '';
    }
    if (selectedPeople.length === 1) return nameOf(people, selectedPeople[0]);
    return selectedPeople.length > 1 ? 'Multiple_Users' : '';
  };
  const exportRows = () => downloadWorklogsWorkbook({ scope, rows, from, to, label: exportLabel(), projects });

  // ---- Layout ----
  const tableRef = useRef(null);
  const fillHeight = useFillViewportHeight(tableRef, [loading, worklogs.length, selectedRows.length > 0, scope]);
  const periodLabel = fullMonth ? `${MONTH_NAMES[fullMonth.month - 1]} ${fullMonth.year}` : `${shortDate(from)} – ${shortDate(to)}`;

  let emptyState = null;
  if (scope === 'mine' && !mineId) emptyState = 'Waiting for authentication to load…';
  else if (scope === 'projects' && projectsReady && !query) {
    emptyState = seesAllProjects ? 'Choose one or more projects to see their worklogs.' : 'There are no projects you manage or lead to show here.';
  } else if (scope === 'people' && peopleReady && !people.length) emptyState = 'You have no reports yet.';

  const scopeFilter =
    scope === 'projects' ? (
      <MultiSelectFilter
        label="Projects"
        allLabel={seesAllProjects ? 'Choose projects' : 'All projects'}
        icon={<ProjectIcon />}
        options={projects.map((p) => ({ id: p.id, name: p.name, caption: customerName(p) }))}
        value={selectedProjects}
        onChange={setSelectedProjects}
        disabled={!projectsReady}
        width={280}
      />
    ) : scope === 'people' ? (
      <>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={peopleMode}
          onChange={(_, v) => {
            if (!v) return;
            setPeopleMode(v);
            setSelectedPeople(EMPTY);
          }}
          sx={segmentedSx}
        >
          <ToggleButton value="direct">Direct reports</ToggleButton>
          <ToggleButton value="all">All reports</ToggleButton>
        </ToggleButtonGroup>
        <MultiSelectFilter
          label="People"
          allLabel={peopleReady ? `All ${modePeople.length} people` : 'Loading…'}
          icon={<PersonIcon />}
          options={peopleOptions}
          value={selectedPeople}
          onChange={setSelectedPeople}
          disabled={!peopleReady}
          width={240}
        />
      </>
    ) : null;

  return (
    <PageContainer>
      <PageHeader
        title={TITLES[scope]}
        subtitle={`${periodLabel}${!loading && query ? ` · ${num(worklogs.length)} worklog${worklogs.length === 1 ? '' : 's'}` : ''}`}
      >
          <MonthPicker year={fullMonth ? fullMonth.year : fromYear} month={fullMonth ? fullMonth.month : fromMonth} onChange={(y, m) => setPeriod(monthRange(y, m).from, monthRange(y, m).to)} />
          <Button
            variant="outlined"
            startIcon={<RangeIcon />}
            onClick={(e) => {
              setDraftRange({ from, to });
              setRangeAnchor(e.currentTarget);
            }}
            sx={{ textTransform: 'none', height: 40, fontWeight: 600, color: fullMonth ? INK : GREEN_DARK, borderColor: fullMonth ? 'divider' : GREEN }}
          >
            {fullMonth ? 'Custom range' : `${shortDate(from)} – ${shortDate(to)}`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportRows}
            disabled={!rows.length}
            sx={{ textTransform: 'none', height: 40, color: INK, borderColor: 'divider', fontWeight: 600 }}
          >
            Export
          </Button>
      </PageHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <Popover open={!!rangeAnchor} anchorEl={rangeAnchor} onClose={() => setRangeAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 260 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Custom range</Typography>
          <TextField type="date" size="small" label="From" value={draftRange.from} onChange={(e) => setDraftRange((r) => ({ ...r, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label="To" value={draftRange.to} onChange={(e) => setDraftRange((r) => ({ ...r, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <Button
            variant="contained"
            color="success"
            disabled={!isDateKey(draftRange.from) || !isDateKey(draftRange.to) || draftRange.to < draftRange.from}
            onClick={() => {
              setPeriod(draftRange.from, draftRange.to);
              setRangeAnchor(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Filtri su una riga, applicati subito */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {scopeFilter}
        {scope !== 'projects' && (
          <MultiSelectFilter label="Projects" allLabel="All projects" icon={<ProjectIcon />} options={projectOptions} value={projectFilter} onChange={setProjectFilter} width={260} />
        )}
        {scope === 'projects' && (
          <MultiSelectFilter label="People" allLabel="Everyone" icon={<PersonIcon />} options={personOptions} value={personFilter} onChange={setPersonFilter} width={240} />
        )}
        <MultiSelectFilter label="Activities" allLabel="All activities" icon={<ActivityIcon />} options={activityOptions} value={activityFilter} onChange={setActivityFilter} width={260} />
        <SearchFilter value={search} onChange={setSearch} placeholder="Search tickets and descriptions" width={300} />
      </Box>

      {(scopeError || loadError) && <Alert severity="error">{scopeError || loadError}</Alert>}
      {data.total > ROW_LIMIT && data.key === loadKey && (
        <Alert severity="warning">
          Showing the first {num(ROW_LIMIT)} of {num(data.total)} worklogs: choose a shorter period or fewer {scope === 'people' ? 'people' : 'projects'}.
        </Alert>
      )}

      {emptyState ? (
        <Alert severity="info">{emptyState}</Alert>
      ) : (
        <>
          {showDayStrip && <DayStrip from={from} to={to} rows={filteredRows} value={dayRange} onChange={setDayRange} />}

          {/* Sintesi del periodo filtrato */}
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <StatCard icon={<HoursIcon />} color={GREEN} label="Hours" value={hoursLabel(stats.minutes)} unit="h" sub={`${num(stats.minutes)} minutes`} loading={loading} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard icon={<WorklogsIcon />} color="#3B82F6" label="Worklogs" value={num(rows.length)} sub={rows.length === worklogs.length ? `in ${periodLabel}` : `of ${num(worklogs.length)} in the period`} loading={loading} />
            </Grid>
            <Grid item xs={6} md={3}>
              {scope === 'mine' ? (
                <StatCard icon={<DaysIcon />} color="#2A9D8F" label="Days" value={stats.days} sub="with at least one worklog" loading={loading} />
              ) : (
                <StatCard icon={<PeopleIcon />} color="#2A9D8F" label="People" value={stats.users} sub="with worklogs" loading={loading} />
              )}
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard icon={<TicketIcon />} color="#E9A23B" label="Tickets" value={num(stats.tickets)} sub="distinct ticket IDs" loading={loading} />
            </Grid>
          </Grid>

          {canChangeTickets && selectedRows.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1, borderRadius: 2, bgcolor: GREEN_SOFT, color: GREEN_DARK }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                {num(selectedRows.length)} selected · {hoursLabel(selectedRows.reduce((s, w) => s + (Number(w.effort) || 0), 0))} h
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" color="success" startIcon={<TicketIcon />} onClick={() => setChangeOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
                Change ticket
              </Button>
              <Button onClick={() => setSelectedIds(new Set())} sx={{ textTransform: 'none', color: GREEN_DARK }}>
                Clear
              </Button>
            </Box>
          )}

          <Box ref={tableRef} sx={{ position: 'relative' }}>
            {loading && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--rt-overlay)', borderRadius: 3 }}>
                <CircularProgress color="success" />
              </Box>
            )}
            <WorklogTable
              rows={rows}
              scope={scope}
              selectable={canChangeTickets}
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              onEdit={startEdit}
              onDelete={setToDelete}
              lockReason={lockReason}
              maxHeight={fillHeight ? fillHeight - 48 : undefined}
            />
          </Box>
        </>
      )}

      {canChangeTickets && (
        <ChangeTicketDialog
          open={changeOpen}
          rows={selectedRows}
          projects={projects}
          managedProjects={managedProjects}
          activities={options.activities}
          onClose={() => setChangeOpen(false)}
          onDone={({ updated, errors }) => {
            setChangeOpen(false);
            setSnack(errors ? `${updated} updated, ${errors} failed` : `${updated} worklog${updated === 1 ? '' : 's'} updated`);
            refresh();
          }}
        />
      )}

      {scope === 'mine' && (
        <>
          <WorklogEditDialog
            open={!!editing}
            value={editing?.value}
            errors={editErrors}
            onChange={changeEdit}
            onClose={() => setEditing(null)}
            onSave={saveEdit}
            saving={editSaving}
            projects={editing && editProject && !editProjects.some((p) => p.id === editProject.id) ? [...editProjects, editProject] : editProjects}
            activities={activitiesFor(editing?.value.projectId)}
            effortTypes={options.effortTypes}
            project={editProject}
            activity={editActivity}
            prefix={editPrefix}
            dayLabel={editDate ? editDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
          />
          <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>Delete worklog?</DialogTitle>
            <DialogContent>
              {toDelete && (
                <Typography sx={{ fontSize: 14, color: INK }}>
                  {formatMinutes(toDelete.effort)} on <strong>{toDelete.project_name}</strong> · {toDelete.activity_name} · {shortDate(toDelete.dateKey)}
                  {toDelete.description ? ` — ${toDelete.description}` : ''}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button color="inherit" onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
                {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      </Box>
    </PageContainer>
  );
}
